/**
 * @file accountCodes.js
 * @version 1.3.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-03-31
 * @modified 2026-08-20
 * @description Xero account code display names. Lookups are case-insensitive
 *              (matches the citext storage in Postgres).
 */

const ACCOUNT_NAMES_RAW = {
  '200': 'Sales',
  '200a1': 'Livestock Equipment sales',
  '200b': 'Garden Products Sales',
  '200b1': 'Equine Equipment sales',
  '200c': 'Animal Bedding sales',
  '200c1': 'Fertiliser Sales',
  '200d': 'Building Supplies sales',
  '200d1': 'Pet Accessories sales',
  '200e': 'Cat Food sales',
  '200f': 'Cat Litter sales',
  '200f1': 'Pet Food Sales',
  '200g': 'Dog Food sales',
  '200h': 'Dog Treats sales',
  '200i': 'Equine Feed sales',
  '200j': 'Heating Fuel sales',
  '200k': 'Licks & Blocs sales',
  '200m': 'Livestock Feed sales',
  '200n': 'Livestock Feed - Totes sales',
  '200p': 'Milk Powder Wards sales',
  '200pp': 'Colostrum sales',
  '200q': 'Poultry food sales',
  '200r': 'Rodenticides sales',
  '200s': 'Special sales',
  '200t': 'Wet Dog Food sales',
  '200v': 'Wild Bird Food sales',
  '200w': 'Seed sales',
  '200z': 'Oils and Lubricants sales',
  '201': 'Compound sales',
  '202': 'Blend sales',
  '204': 'Bulk Straights sales',
  '206': 'Mineral sales',
  '207': 'Compound bag sales',
  '207a': 'Blend tote bag sales',
  '208': 'Other sales (colostrum/buckets)',
  '209': 'Fat sales',
  '210': 'Additive sales',
  '213': 'Haulage income',
  '310': 'Cost of Goods Sold',
  '310a1': 'Livestock Equipment purchases',
  '310b': 'Garden Products purchases',
  '310b1': 'Equine Equipment purchases',
  '310c': 'Animal Bedding purchases',
  '310c1': 'Fertiliser purchases',
  '310d': 'Building Supplies purchases',
  '310d1': 'Pet Accessories purchases',
  '310e': 'Cat Food purchases',
  '310f': 'Cat Litter purchases',
  '310f1': 'Pet Food Purchases',
  '310g': 'Dog Food purchases',
  '310h': 'Dog Treats purchases',
  '310i': 'Equine Feed purchases',
  '310j': 'Heating Fuel purchases',
  '310k': 'Licks & Blocs purchases',
  '310m': 'Livestock Feed purchases',
  '310n': 'Livestock Feed - Totes purchases',
  '310p': 'Milk Powder Wards purchases',
  '310pp': 'Colostrum purchases',
  '310q': 'Poultry food purchases',
  '310r': 'Rodenticides purchases',
  '310s': 'Special purchases',
  '310t': 'Wet Dog Food purchases',
  '310v': 'Wild Bird Food purchases',
  '310w': 'Seed Purchases',
  '310z': 'Oils and Lubricants purchases',
  '311': 'Compound COGS',
  '312': 'Blend COGS',
  '314': 'Bulk Straights COGS',
  '315': 'Milk powder COGS',
  '316': 'Mineral COGS',
  '317': 'Compound Bags COGS',
  '317a': 'Blend tote Bag COGS',
  '318': 'Other purchases COGS',
  '319': 'Fat COGS',
  '319a': 'Additive COGS',
  '486': 'HGCA Levy',
  '778': 'Cash Account'
};

const ACCOUNT_NAMES_LOWER = Object.keys(ACCOUNT_NAMES_RAW).reduce((acc, k) => {
  acc[k.toLowerCase()] = ACCOUNT_NAMES_RAW[k];
  return acc;
}, {});

export const ACCOUNT_NAMES = new Proxy(ACCOUNT_NAMES_RAW, {
  get(target, prop) {
    if (typeof prop !== 'string') return target[prop];
    return ACCOUNT_NAMES_LOWER[prop.toLowerCase()] ?? target[prop];
  },
  has(target, prop) {
    if (typeof prop !== 'string') return prop in target;
    return prop.toLowerCase() in ACCOUNT_NAMES_LOWER;
  }
});

export function getAccountLabel(code) {
  if (!code) return '-';
  const name = ACCOUNT_NAMES_LOWER[String(code).toLowerCase()];
  return name ? `${code} — ${name}` : code;
}

export function getAccountName(code) {
  if (!code) return '';
  return ACCOUNT_NAMES_LOWER[String(code).toLowerCase()] || code;
}

/**
 * Sales-account-code groupings that back each contract-pricing tab. A tab
 * lists / creates contracts for products whose inventory sales_account_code
 * falls in its bucket. Codes are compared case-insensitively (citext storage
 * uses uppercase, e.g. '200N' / '207A').
 */
export const CONTRACT_CATEGORY_CODES = {
  straights: ['204'],
  blends: ['202'],
  compounds: ['201'],
  minerals: ['206'],
  milkPowder: ['200P'],
  bags: ['207', '207A', '200N'],
};

/**
 * Codes that already have their own dedicated pricing area and are therefore
 * EXCLUDED from the catch-all "Other" contract-pricing tab. Everything not in
 * this set (and with a non-empty code) is treated as "Other".
 *   201/202/204 — compounds / blends / straights
 *   206         — minerals
 *   200P        — milk powder (its own Contract Pricing tab)
 *   210         — additives (its own screen)
 *   207/207A/200N — bags
 */
export const OTHER_EXCLUDED_CODES = [
  '201', '202', '204', '206', '200P', '210', '207', '207A', '200N',
];

const OTHER_EXCLUDED_LOWER = new Set(OTHER_EXCLUDED_CODES.map(c => c.toLowerCase()));

/**
 * Sales accounts whose products can carry additives.
 *
 * Additives are dosed into compound feed at the mill, so they only apply to a
 * product that is milled to order: 201 Compound sales and 207 Compound bag
 * sales. Anything sold from stock cannot be — 200M (Livestock Feed) and 200N
 * (Livestock Feed - Totes) in particular are bagged stock lines that used to be
 * offered an additive picker they had no use for. Every one of the 28 existing
 * contract-additive rows sits on 201, which matches.
 */
export const ADDITIVE_ELIGIBLE_CODES = ['201', '207'];

/** True when a product's sales account allows additives. */
export function allowsAdditives(salesAccountCode) {
  return codeInList(salesAccountCode, ADDITIVE_ELIGIBLE_CODES);
}

/** Case-insensitive membership test for a code against a list of codes. */
export function codeInList(code, list) {
  if (!code || !Array.isArray(list)) return false;
  const c = String(code).toLowerCase();
  return list.some(x => String(x).toLowerCase() === c);
}

/** True when a code belongs to the catch-all "Other" tab. */
export function isOtherCode(code) {
  if (!code) return false;
  return !OTHER_EXCLUDED_LOWER.has(String(code).toLowerCase());
}
